import { create } from "zustand";
import type {
  DraftCellsWire,
  GuessEntry,
  GuessPattern,
  MatchFound,
  MatchRecords,
  MatchCategory,
  MatchOver,
  MatchState,
  OpponentWatchState,
  PublicUser,
  DuelRules,
  DuelSessionScore,
} from "@mk/shared";
import { MAX_ATTEMPTS } from "@mk/shared";
import { getSocket, serverNow } from "../../lib/socket.ts";
import { useSession } from "../../lib/session.ts";
import { recordCompletedGame } from "../../lib/adGate.ts";
import { errorMessage, messages } from "../../i18n/index.ts";
import { duelRevealDuration, prefersReducedMotion } from "./revealTiming.ts";

/**
 * Düello durumu neden global?
 *
 * Maç her an başlayabilir: arkadaşın meydan okumasını kabul ettiğinde sunucu
 * `match:found`'u ANINDA iki tarafa da yollar. Dinleyici yalnızca düello ekranı
 * açıkken kurulu olsaydı bu olay kaçardı — sunucuda maç açık kalır, oyuncu
 * "zaten maçtasın" uyarısıyla 3 dakika kilitli kalırdı.
 *
 * Aynı sebeple rövanş teklifi de burada dinleniyor: teklif geldiğinde oyuncu
 * ana ekranda olabilir.
 */

export type DuelPhase = "idle" | "searching" | "countdown" | "live" | "over";

export interface DuelMatch {
  matchId: string;
  opponent: PublicUser;
  startsAt: number;
  endsAt: number;
  rules: DuelRules;
  category: MatchCategory;
  sessionScore: DuelSessionScore | null;
  records: MatchRecords | null;
}

export interface RematchOffer {
  from: PublicUser;
  matchId: string;
  expiresAt: number;
  rules: DuelRules;
  category: MatchCategory;
}

interface DuelState {
  phase: DuelPhase;
  match: DuelMatch | null;
  rows: GuessEntry[];
  opponentRows: GuessPattern[];
  /** Rakip kaçıncı denemede buldu; bulamadıysa null. */
  opponentSolvedAt: number | null;
  /** Sunucunun bu oyuncu için izin verdiği son tahmin sayısı. */
  attemptLimit: number;
  /** Son tahmin animasyonu bitene kadar sonuç/rakip görünümüne geçilmez. */
  ownRevealUntil: number;
  result: MatchOver | null;
  watchers: { total: number; letters: number; opponent: boolean };
  opponentWatch: OpponentWatchState | null;
  opponentWatchRequestedFor: string | null;
  /** Kullanıcının hükmen çıkış istediği maç; sonucu bu cihazda göstermeyiz. */
  exitingMatchId: string | null;

  /** Sana gelen rövanş teklifi. */
  rematchOffer: RematchOffer | null;
  /** Senin rövanş istediğin maçın kimliği — düğme "bekleniyor"a döner. */
  rematchSentFor: string | null;
  /** "Rakibin reddetti" gibi tek seferlik bilgi. */
  rematchNotice: string | null;

  setPhase: (phase: DuelPhase) => void;
  syncBoard: (matchId: string, guesses: GuessEntry[]) => void;
  setDeadline: (matchId: string, endsAt: number) => void;
  startFrom: (found: MatchFound) => void;
  setRecords: (matchId: string, records: MatchRecords) => void;
  restoreFrom: (state: MatchState) => void;
  applyProgress: (row: number, pattern: GuessPattern) => void;
  markOpponentSolved: (attempts: number) => void;
  setAttemptLimit: (matchId: string, attemptLimit: number) => void;
  setWatchers: (matchId: string, total: number, letters: number, opponent: boolean) => void;
  beginOpponentWatch: (matchId: string) => void;
  openOpponentWatch: (state: OpponentWatchState) => void;
  applyOpponentWatchUpdate: (payload: OpponentWatchUpdate) => void;
  applyOpponentWatchDraft: (payload: OpponentWatchDraft) => void;
  closeOpponentWatch: () => void;
  finish: (result: MatchOver) => void;
  beginExit: (matchId: string) => void;
  cancelExit: () => void;
  setRematchSent: (matchId: string | null) => void;
  clearRematchOffer: () => void;
  clearRematchNotice: () => void;
  reset: () => void;
}

const EMPTY = {
  match: null,
  rows: [] as GuessEntry[],
  opponentRows: [] as GuessPattern[],
  opponentSolvedAt: null,
  attemptLimit: MAX_ATTEMPTS,
  ownRevealUntil: 0,
  result: null,
  watchers: { total: 0, letters: 0, opponent: false },
  opponentWatch: null,
  opponentWatchRequestedFor: null,
  exitingMatchId: null,
  rematchOffer: null,
  rematchSentFor: null,
  rematchNotice: null,
};

interface OpponentWatchUpdate {
  matchId: string;
  userId: string;
  row: number;
  word: string;
  pattern: GuessPattern;
  solved: boolean;
  attemptLimit: number;
}

interface OpponentWatchDraft {
  matchId: string;
  userId: string;
  row: number;
  cells: DraftCellsWire;
}

type PendingOpponentEvent =
  | { type: "update"; payload: OpponentWatchUpdate }
  | { type: "draft"; payload: OpponentWatchDraft };

let pendingOpponentEvents: PendingOpponentEvent[] = [];

function applyWatchUpdate(
  current: OpponentWatchState,
  payload: OpponentWatchUpdate,
): OpponentWatchState {
  if (payload.matchId !== current.matchId || payload.userId !== current.userId) return current;
  const guesses = [...current.guesses];
  guesses[payload.row] = { word: payload.word, pattern: payload.pattern };
  return { ...current, guesses, draft: null, attemptLimit: payload.attemptLimit };
}

function applyWatchDraft(
  current: OpponentWatchState,
  payload: OpponentWatchDraft,
): OpponentWatchState {
  if (
    payload.matchId !== current.matchId ||
    payload.userId !== current.userId ||
    payload.row !== current.guesses.length
  ) {
    return current;
  }
  return {
    ...current,
    draft: payload.cells.every((cell) => cell === null)
      ? null
      : { row: payload.row, cells: payload.cells },
  };
}

export const useDuelStore = create<DuelState>((set) => ({
  phase: "idle",
  ...EMPTY,

  setPhase: (phase) => set({ phase }),

  /**
   * Tahtanın TEK kaynağı. Tahmin ack'inden satır eklemiyoruz: iki yol birden
   * yazınca, hangisi önce gelirse ona göre satır çiftleniyordu.
   */
  syncBoard: (matchId, guesses) =>
    set((s) => {
      if (s.match?.matchId !== matchId) return {};
      const lastGuess = guesses.at(-1);
      const addedAcceptedGuess = guesses.length > s.rows.length;
      const turnFinished = Boolean(
        lastGuess &&
          (lastGuess.pattern.every((state) => state === "correct") ||
            guesses.length >= s.attemptLimit),
      );
      return {
        rows: guesses,
        ownRevealUntil:
          addedAcceptedGuess && turnFinished && lastGuess
            ? Date.now() + duelRevealDuration(lastGuess.pattern, prefersReducedMotion())
            : s.ownRevealUntil,
      };
    }),

  /** Rakip bulunca süre kısalır; saatin kaynağı her zaman sunucu. */
  setDeadline: (matchId, endsAt) =>
    set((s) =>
      s.match?.matchId === matchId ? { match: { ...s.match, endsAt } } : {},
    ),

  startFrom: (found) =>
    set(() => {
      pendingOpponentEvents = [];
      return {
        ...EMPTY,
        attemptLimit: found.attemptLimit,
        match: {
          matchId: found.matchId,
          opponent: found.opponent,
          startsAt: found.startsAt,
          endsAt: found.endsAt,
          rules: found.rules,
          category: found.category,
          sessionScore: found.sessionScore,
          records: found.records ?? null,
        },
        phase: serverNow() < found.startsAt ? "countdown" : "live",
      };
    }),

  restoreFrom: (state) =>
    set((current) => {
      const sameMatch = current.match?.matchId === state.matchId;
      if (!sameMatch) pendingOpponentEvents = [];
      return {
        match: {
          matchId: state.matchId,
          opponent: state.opponent,
          startsAt: state.startsAt,
          endsAt: state.endsAt,
          rules: state.rules,
          category: state.category,
          sessionScore: state.sessionScore,
          records: state.records ?? null,
        },
        rows: state.guesses,
        opponentRows: state.opponentRows,
        opponentSolvedAt: state.opponentSolved ? state.opponentRows.length : null,
        attemptLimit: state.attemptLimit,
        ownRevealUntil: 0,
        result: null,
        opponentWatch: sameMatch ? current.opponentWatch : null,
        opponentWatchRequestedFor: sameMatch ? current.opponentWatchRequestedFor : null,
        exitingMatchId: null,
        rematchOffer: null,
        rematchSentFor: null,
        phase: serverNow() < state.startsAt ? "countdown" : "live",
      };
    }),

  setRecords: (matchId, records) => set((current) => current.match?.matchId === matchId
    ? { match: { ...current.match, records } } : {}),

  applyProgress: (row, pattern) =>
    set((s) => {
      const opponentRows = [...s.opponentRows];
      opponentRows[row] = pattern;
      return { opponentRows };
    }),

  markOpponentSolved: (attempts) => set({ opponentSolvedAt: attempts }),

  setAttemptLimit: (matchId, attemptLimit) =>
    set((state) =>
      state.match?.matchId === matchId ? { attemptLimit } : {},
    ),

  setWatchers: (matchId, total, letters, opponent) =>
    set((state) =>
      state.match?.matchId === matchId
        ? { watchers: { total, letters, opponent } }
        : {},
    ),

  beginOpponentWatch: (matchId) => {
    pendingOpponentEvents = [];
    set({ opponentWatch: null, opponentWatchRequestedFor: matchId });
  },

  openOpponentWatch: (state) =>
    set((current) => {
      if (current.opponentWatchRequestedFor !== state.matchId) return {};
      let opponentWatch = state;
      for (const event of pendingOpponentEvents) {
        opponentWatch =
          event.type === "update"
            ? applyWatchUpdate(opponentWatch, event.payload)
            : applyWatchDraft(opponentWatch, event.payload);
      }
      pendingOpponentEvents = [];
      return { opponentWatch };
    }),

  applyOpponentWatchUpdate: (payload) =>
    set((current) => {
      if (current.opponentWatch) {
        return { opponentWatch: applyWatchUpdate(current.opponentWatch, payload) };
      }
      if (current.opponentWatchRequestedFor === payload.matchId) {
        pendingOpponentEvents.push({ type: "update", payload });
      }
      return {};
    }),

  applyOpponentWatchDraft: (payload) =>
    set((current) => {
      if (current.opponentWatch) {
        return { opponentWatch: applyWatchDraft(current.opponentWatch, payload) };
      }
      if (current.opponentWatchRequestedFor === payload.matchId) {
        pendingOpponentEvents.push({ type: "draft", payload });
      }
      return {};
    }),

  closeOpponentWatch: () => {
    pendingOpponentEvents = [];
    set({ opponentWatch: null, opponentWatchRequestedFor: null });
  },

  finish: (result) => {
    pendingOpponentEvents = [];
    if (result.reason !== "forfeit") recordCompletedGame(`duel:${result.matchId}`);
    set({
      result,
      phase: "over",
      watchers: { total: 0, letters: 0, opponent: false },
      opponentWatch: null,
      opponentWatchRequestedFor: null,
    });
  },
  beginExit: (matchId) => set({ exitingMatchId: matchId }),
  cancelExit: () => set({ exitingMatchId: null }),

  setRematchSent: (matchId) => set({ rematchSentFor: matchId }),
  clearRematchOffer: () => set({ rematchOffer: null }),
  clearRematchNotice: () => set({ rematchNotice: null }),

  reset: () => {
    pendingOpponentEvents = [];
    set({ phase: "idle", ...EMPTY });
  },
}));

let detach: (() => void) | null = null;

/** Oturum açıldığında bir kez çağrılır; çıkışta dönen fonksiyon çalıştırılır. */
export function attachDuelListeners(): () => void {
  if (detach) return detach;

  const socket = getSocket();
  const store = useDuelStore.getState;

  const onFound = (payload: MatchFound) => store().startFrom(payload);
  const onRecords = (payload: { matchId: string; records: MatchRecords }) => store().setRecords(payload.matchId, payload.records);
  const onState = (payload: MatchState) => store().restoreFrom(payload);
  const onProgress = (payload: { row: number; pattern: GuessPattern }) =>
    store().applyProgress(payload.row, payload.pattern);
  const onOpponentSolved = (payload: { attempts: number }) =>
    store().markOpponentSolved(payload.attempts);
  const onAttemptLimit = (payload: { matchId: string; attemptLimit: number }) =>
    store().setAttemptLimit(payload.matchId, payload.attemptLimit);
  const onBoardSync = (payload: { matchId: string; guesses: GuessEntry[] }) =>
    store().syncBoard(payload.matchId, payload.guesses);
  const onDeadline = (payload: { matchId: string; endsAt: number }) =>
    store().setDeadline(payload.matchId, payload.endsAt);
  const onWatchers = (payload: {
    matchId: string;
    total: number;
    letters: number;
    opponent: boolean;
  }) => store().setWatchers(payload.matchId, payload.total, payload.letters, payload.opponent);
  const onOpponentWatchUpdate = (payload: OpponentWatchUpdate) =>
    store().applyOpponentWatchUpdate(payload);
  const onOpponentWatchDraft = (payload: OpponentWatchDraft) =>
    store().applyOpponentWatchDraft(payload);
  const onOver = (payload: MatchOver) => {
    if (store().exitingMatchId !== payload.matchId) store().finish(payload);
    void useSession.getState().refreshStats();
  };

  const onRematchOffered = (payload: RematchOffer) =>
    useDuelStore.setState({ rematchOffer: payload, rematchNotice: null });
  const onRematchDeclined = () =>
    useDuelStore.setState({ rematchSentFor: null, rematchNotice: messages.rematch.declined });
  const onRematchCancelled = (payload: { reason: Parameters<typeof errorMessage>[0] }) =>
    useDuelStore.setState({
      rematchOffer: null,
      rematchSentFor: null,
      rematchNotice: errorMessage(payload.reason),
    });

  socket.on("match:found", onFound);
  socket.on("match:records", onRecords);
  socket.on("match:state", onState);
  socket.on("opponent:progress", onProgress);
  socket.on("opponent:solved", onOpponentSolved);
  socket.on("match:attempt-limit", onAttemptLimit);
  socket.on("board:sync", onBoardSync);
  socket.on("match:deadline", onDeadline);
  socket.on("match:watchers", onWatchers);
  socket.on("opponent:watch:update", onOpponentWatchUpdate);
  socket.on("opponent:watch:draft", onOpponentWatchDraft);
  socket.on("match:over", onOver);
  socket.on("rematch:offered", onRematchOffered);
  socket.on("rematch:declined", onRematchDeclined);
  socket.on("rematch:cancelled", onRematchCancelled);

  detach = () => {
    socket.off("match:found", onFound);
    socket.off("match:records", onRecords);
    socket.off("match:state", onState);
    socket.off("opponent:progress", onProgress);
    socket.off("opponent:solved", onOpponentSolved);
    socket.off("match:attempt-limit", onAttemptLimit);
    socket.off("board:sync", onBoardSync);
    socket.off("match:deadline", onDeadline);
    socket.off("match:watchers", onWatchers);
    socket.off("opponent:watch:update", onOpponentWatchUpdate);
    socket.off("opponent:watch:draft", onOpponentWatchDraft);
    socket.off("match:over", onOver);
    socket.off("rematch:offered", onRematchOffered);
    socket.off("rematch:declined", onRematchDeclined);
    socket.off("rematch:cancelled", onRematchCancelled);
    detach = null;
  };

  return detach;
}
